import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, ButtonGroup, Row, Col, Label, Input, Table, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { fDMA } from "../../../utils/fecha";

/**
 * Gastos recurrentes (renta, luz, internet...): un catálogo con todo lo que
 * lleva cada gasto, su monto estimado y su frecuencia, y una pestaña con los
 * que ya tocan para registrarlos ajustando el monto real. Registrar crea un
 * gasto normal (con su movimiento de banco) y recorre la próxima fecha.
 */

const FRECUENCIAS = ["Semanal", "Catorcenal", "Quincenal", "Mensual", "Bimestral", "Trimestral", "Semestral", "Anual"];
const TASA_IVA = 0.16;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);
const ivaDe = (subtotal, llevaIva, ivaManual) =>
  llevaIva === "Si" ? r2((Number(subtotal) || 0) * TASA_IVA) : llevaIva === "No" ? 0 : r2(ivaManual);

const VACIO = {
  _id: "",
  nombre: "",
  descripcion: "",
  empresas: "",
  bancos: "",
  categorias: "",
  subCategorias: "",
  solicitantes: "",
  llevaIva: "Si",
  subtotal: 0,
  iva: 0,
  frecuencia: "Mensual",
  fechaInicio: moment().format("YYYY-MM-DD"),
};

function GastosRecurrentes() {
  const { user } = useContext(AuthContext);
  const URL = process.env.REACT_APP_URL_GASTOS_RECURRENTES;
  const AREAS_GENERAL = process.env.REACT_APP_AREAS_GENERAL;
  const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

  const [tab, setTab] = useState("pendientes");
  const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));
  const [pendientes, setPendientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const [categorias, setCategorias] = useState([]);
  const [subCategorias, setSubCategorias] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [solicitantes, setSolicitantes] = useState([]);

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(VACIO);

  // Catálogos para el formulario (los mismos de Nuevo Gasto)
  useEffect(() => {
    const trae = (url, set) =>
      axios.get(url, auth()).then((r) => set(r.data || [])).catch((e) => console.log(e));
    trae(process.env.REACT_APP_URL_CATEGORIAS, setCategorias);
    trae(process.env.REACT_APP_URL_SUB_CATEGORIAS, setSubCategorias);
    trae(process.env.REACT_APP_URL_EMPRESAS, setEmpresas);
    trae(process.env.REACT_APP_URL_BANCOS, setBancos);
    trae(process.env.REACT_APP_URL_SOLICITANTES, setSolicitantes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargaCatalogo = useCallback(() => {
    axios
      .get(URL, auth())
      .then((r) => setCatalogo(r.data || []))
      .catch((e) => console.log(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [URL]);

  const cargaPendientes = useCallback(() => {
    axios
      .get(`${URL}Pendientes/${hasta}`, auth())
      .then((r) =>
        setPendientes(
          (r.data || []).map((g) => ({
            ...g,
            sel: true,
            fechaReg: g.proximaFecha,
            subtotalReg: Number(g.subtotal) || 0,
            ivaReg: Number(g.iva) || 0,
          }))
        )
      )
      .catch((e) => console.log(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [URL, hasta]);

  useEffect(() => {
    cargaCatalogo();
  }, [cargaCatalogo]);
  useEffect(() => {
    cargaPendientes();
  }, [cargaPendientes]);

  const cambiaPendiente = (id, cambios) =>
    setPendientes((prev) => prev.map((p) => (p._id === id ? { ...p, ...cambios } : p)));

  const seleccionados = pendientes.filter((p) => p.sel);
  const totalSeleccionado = seleccionados.reduce((t, p) => t + r2(p.subtotalReg) + r2(p.ivaReg), 0);

  async function registrar() {
    if (!seleccionados.length) return Swal.fire({ icon: "info", title: "Selecciona al menos uno" });
    const malos = seleccionados.filter((p) => !(r2(p.subtotalReg) + r2(p.ivaReg) > 0));
    if (malos.length) {
      return Swal.fire({ icon: "warning", title: "Hay montos en cero", text: malos.map((p) => p.nombre).join(", ") });
    }
    const ok = await Swal.fire({
      icon: "question",
      title: `Registrar ${seleccionados.length} gastos`,
      text: `Total ${money(totalSeleccionado)}. Se crea cada gasto con su movimiento de banco.`,
      showCancelButton: true,
      confirmButtonText: "Registrar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const { data } = await axios.post(
        `${URL}Registrar`,
        {
          areas: AREAS_GENERAL,
          renglones: seleccionados.map((p) => ({
            id: p._id,
            fecha: p.fechaReg,
            subtotal: r2(p.subtotalReg),
            iva: r2(p.ivaReg),
          })),
        },
        auth()
      );
      await Swal.fire({
        icon: data.errores?.length ? "warning" : "success",
        title: `Se registraron ${data.hechos?.length || 0} gastos`,
        html: (data.errores || []).length
          ? "No se pudieron:<br/>" +
            data.errores.map((e) => `${pendientes.find((p) => p._id === e.id)?.nombre || e.id}: ${e.message}`).join("<br/>")
          : "",
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo registrar", text: err?.response?.data?.message || err.message });
    } finally {
      setGuardando(false);
      cargaPendientes();
      cargaCatalogo();
    }
  }

  async function omitir(p) {
    const ok = await Swal.fire({
      icon: "question",
      title: `Omitir ${p.nombre}`,
      text: `No se registra gasto para el ${fDMA(p.proximaFecha)}: se pasa al siguiente periodo.`,
      showCancelButton: true,
      confirmButtonText: "Omitir",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.patch(`${URL}Omitir/${p._id}`, {}, auth());
      cargaPendientes();
      cargaCatalogo();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo omitir", text: err?.response?.data?.message || err.message });
    }
  }

  // ── Catálogo
  function abrirNuevo() {
    setForm(VACIO);
    setModal(true);
  }
  function abrirEditar(g) {
    setForm({
      _id: g._id,
      nombre: g.nombre || "",
      descripcion: g.descripcion || "",
      empresas: g.empresas?.[0]?._id || "",
      bancos: g.bancos?.[0]?._id || "",
      categorias: g.categorias?.[0]?._id || "",
      subCategorias: g.subCategorias?.[0]?._id || "",
      solicitantes: g.solicitantes?.[0]?._id || "",
      llevaIva: g.llevaIva || "Si",
      subtotal: Number(g.subtotal) || 0,
      iva: Number(g.iva) || 0,
      frecuencia: g.frecuencia || "Mensual",
      fechaInicio: g.fechaInicio || moment().format("YYYY-MM-DD"),
    });
    setModal(true);
  }
  const cambiaForm = (cambios) =>
    setForm((f) => {
      const n = { ...f, ...cambios };
      n.iva = ivaDe(n.subtotal, n.llevaIva, n.iva);
      return n;
    });

  async function guardarForm() {
    if (!form.nombre.trim() || !form.categorias || !form.fechaInicio) {
      return Swal.fire({ icon: "warning", title: "Falta el nombre, la categoría o la fecha de inicio" });
    }
    const body = {
      nombre: form.nombre.trim(),
      descripcion: form.descripcion || "",
      empresas: form.empresas,
      bancos: form.bancos,
      categorias: form.categorias,
      subCategorias: form.subCategorias,
      solicitantes: form.solicitantes,
      llevaIva: form.llevaIva,
      subtotal: r2(form.subtotal),
      iva: r2(form.iva),
      total: r2(Number(form.subtotal) + Number(form.iva)),
      frecuencia: form.frecuencia,
      fechaInicio: form.fechaInicio,
    };
    try {
      if (form._id) await axios.patch(`${URL}/${form._id}`, body, auth());
      else await axios.post(URL, body, auth());
      setModal(false);
      cargaCatalogo();
      cargaPendientes();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message });
    }
  }

  async function cambiaActivo(g) {
    try {
      await axios.patch(`${URL}/${g._id}`, { is_active: g.is_active === "Si" ? "No" : "Si" }, auth());
      cargaCatalogo();
      cargaPendientes();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo cambiar", text: err?.response?.data?.message || err.message });
    }
  }

  const nombreDe = (arr) => arr?.[0]?.name || arr?.[0]?.banco || "";

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_gastos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuGastos" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
          </Row>
          <h3 align="center">Gastos Recurrentes</h3>
          <div align="center" style={{ marginBottom: 12 }}>
            <ButtonGroup>
              <Button color={tab === "pendientes" ? "primary" : "secondary"} onClick={() => setTab("pendientes")}>
                Por registrar ({pendientes.length})
              </Button>
              <Button color={tab === "catalogo" ? "primary" : "secondary"} onClick={() => setTab("catalogo")}>
                Catálogo ({catalogo.filter((g) => g.is_active === "Si").length})
              </Button>
            </ButtonGroup>
          </div>

          {tab === "pendientes" ? (
            <>
              <Row>
                <Col md={2}>
                  <Label>Que toquen hasta</Label>
                  <Input bsSize="sm" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
                </Col>
                <Col md={10} style={{ fontSize: 13, color: "#555", paddingTop: 22 }}>
                  Ajusta el monto real de cada uno y registra los seleccionados. Cada uno se guarda como un gasto normal con
                  su movimiento de banco. Si este periodo no se paga, usa <b>Omitir</b>.
                </Col>
              </Row>
              <br />
              {pendientes.length === 0 ? (
                <p align="center" style={{ color: "#6c757d" }}>
                  No hay gastos recurrentes que toquen hasta el {fDMA(hasta)}.
                </p>
              ) : (
                <Table size="sm" striped bordered className="table-responsive-xl">
                  <thead>
                    <tr style={{ fontSize: 12 }}>
                      <th></th>
                      <th>Gasto</th>
                      <th>Frecuencia</th>
                      <th>Toca</th>
                      <th>Categoría</th>
                      <th>Banco</th>
                      <th>Fecha del gasto</th>
                      <th style={{ textAlign: "right" }}>Subtotal</th>
                      <th style={{ textAlign: "right" }}>IVA</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: 13 }}>
                    {pendientes.map((p) => (
                      <tr key={p._id}>
                        <td align="center">
                          <input type="checkbox" checked={p.sel} onChange={(e) => cambiaPendiente(p._id, { sel: e.target.checked })} />
                        </td>
                        <td>
                          <b>{p.nombre}</b>
                          {p.descripcion ? <div style={{ fontSize: 11, color: "#666" }}>{p.descripcion}</div> : null}
                        </td>
                        <td>{p.frecuencia}</td>
                        <td>
                          {fDMA(p.proximaFecha)}
                          {p.periodosPendientes > 1 ? (
                            <div style={{ fontSize: 11, color: "#dc3545" }}>{p.periodosPendientes} periodos atrasados</div>
                          ) : null}
                        </td>
                        <td>{nombreDe(p.categorias)}</td>
                        <td>{nombreDe(p.bancos) || "-"}</td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="date"
                            value={p.fechaReg}
                            onChange={(e) => cambiaPendiente(p._id, { fechaReg: e.target.value })}
                          />
                        </td>
                        <td align="right">
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            step="any"
                            style={{ width: 120, display: "inline-block" }}
                            value={p.subtotalReg}
                            onChange={(e) =>
                              cambiaPendiente(p._id, {
                                subtotalReg: e.target.value,
                                ivaReg: ivaDe(e.target.value, p.llevaIva, p.ivaReg),
                              })
                            }
                          />
                        </td>
                        <td align="right">
                          {p.llevaIva === "Manual" ? (
                            <Input
                              bsSize="sm"
                              type="number"
                              min="0"
                              step="any"
                              style={{ width: 100, display: "inline-block" }}
                              value={p.ivaReg}
                              onChange={(e) => cambiaPendiente(p._id, { ivaReg: e.target.value })}
                            />
                          ) : (
                            money(p.ivaReg)
                          )}
                        </td>
                        <td align="right">
                          <b>{money(r2(p.subtotalReg) + r2(p.ivaReg))}</b>
                          {Math.abs(r2(p.subtotalReg) - Number(p.subtotal || 0)) >= 0.01 ? (
                            <div style={{ fontSize: 11, color: "#6c757d" }}>estimado {money(p.total)}</div>
                          ) : null}
                        </td>
                        <td>
                          <Button size="sm" color="secondary" onClick={() => omitir(p)} title="Este periodo no se paga">
                            Omitir
                          </Button>
                        </td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: "bold" }}>
                      <td colSpan={9} align="right">
                        Total seleccionado ({seleccionados.length})
                      </td>
                      <td align="right">{money(totalSeleccionado)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              )}
              {pendientes.length > 0 ? (
                <div align="right">
                  <Button color="success" disabled={guardando} onClick={registrar}>
                    Registrar seleccionados
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div align="right" style={{ marginBottom: 8 }}>
                <Button size="sm" color="success" onClick={abrirNuevo}>
                  Nuevo gasto recurrente <i className="fas fa-plus"></i>
                </Button>
              </div>
              <Table size="sm" striped bordered className="table-responsive-xl">
                <thead>
                  <tr style={{ fontSize: 12 }}>
                    <th>#</th>
                    <th>Gasto</th>
                    <th>Categoría</th>
                    <th>Empresa</th>
                    <th>Banco</th>
                    <th>Frecuencia</th>
                    <th style={{ textAlign: "right" }}>Monto estimado</th>
                    <th>Próxima fecha</th>
                    <th>Último registro</th>
                    <th>Activo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: 13 }}>
                  {catalogo.map((g) => (
                    <tr key={g._id} style={g.is_active !== "Si" ? { color: "#999" } : undefined}>
                      <td>{g.idGastoRecurrente}</td>
                      <td>
                        <b>{g.nombre}</b>
                        {g.descripcion ? <div style={{ fontSize: 11, color: "#666" }}>{g.descripcion}</div> : null}
                      </td>
                      <td>{nombreDe(g.categorias)}</td>
                      <td>{nombreDe(g.empresas)}</td>
                      <td>{nombreDe(g.bancos) || "-"}</td>
                      <td>{g.frecuencia}</td>
                      <td align="right">{money(g.total)}</td>
                      <td>{g.is_active === "Si" ? fDMA(g.proximaFecha) : "-"}</td>
                      <td>{g.ultimaFecha ? fDMA(g.ultimaFecha) : "-"}</td>
                      <td>{g.is_active}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Button size="sm" color="info" onClick={() => abrirEditar(g)} title="Editar">
                          <i className="far fa-edit"></i>
                        </Button>{" "}
                        <Button
                          size="sm"
                          color={g.is_active === "Si" ? "danger" : "success"}
                          onClick={() => cambiaActivo(g)}
                          title={g.is_active === "Si" ? "Desactivar" : "Activar"}
                        >
                          <i className={g.is_active === "Si" ? "fas fa-ban" : "fas fa-check"}></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          <Modal size="lg" isOpen={modal} toggle={() => setModal(!modal)}>
            <ModalHeader toggle={() => setModal(!modal)}>
              {form._id ? "Editar gasto recurrente" : "Nuevo gasto recurrente"}
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <Label>Nombre</Label>
                  <Input value={form.nombre} placeholder="Ej. Renta bodega" onChange={(e) => cambiaForm({ nombre: e.target.value })} />
                </Col>
                <Col md={6}>
                  <Label>Descripción (sale en cada gasto)</Label>
                  <Input value={form.descripcion} onChange={(e) => cambiaForm({ descripcion: e.target.value })} />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Label>Empresa</Label>
                  <Input type="select" value={form.empresas} onChange={(e) => cambiaForm({ empresas: e.target.value })}>
                    <option value="">Selecciona</option>
                    {empresas.map((a) => (
                      <option key={a._id} value={a._id}>{a.name}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label>Banco</Label>
                  <Input type="select" value={form.bancos} onChange={(e) => cambiaForm({ bancos: e.target.value })}>
                    <option value="">Sin banco</option>
                    {bancos.map((a) => (
                      <option key={a._id} value={a._id}>{a.banco} {a.cuenta}</option>
                    ))}
                  </Input>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Label>Categoría</Label>
                  <Input
                    type="select"
                    value={form.categorias}
                    onChange={(e) => cambiaForm({ categorias: e.target.value, subCategorias: "" })}
                  >
                    <option value="">Selecciona</option>
                    {[...categorias]
                      .sort((a, b) => (a.name > b.name ? 1 : -1))
                      .map((a) => (
                        <option key={a._id} value={a._id}>{a.name}</option>
                      ))}
                  </Input>
                </Col>
                <Col md={6}>
                  <Label>SubCategoría (opcional)</Label>
                  <Input type="select" value={form.subCategorias} onChange={(e) => cambiaForm({ subCategorias: e.target.value })}>
                    <option value="">Sin subcategoría</option>
                    {subCategorias
                      .filter((a) => (a.categorias?.[0]?._id || a.categorias?.[0]) === form.categorias)
                      .map((a) => (
                        <option key={a._id} value={a._id}>{a.name}</option>
                      ))}
                  </Input>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Label>Solicitó / Autorizó</Label>
                  <Input type="select" value={form.solicitantes} onChange={(e) => cambiaForm({ solicitantes: e.target.value })}>
                    <option value="">Selecciona</option>
                    {solicitantes.map((a) => (
                      <option key={a._id} value={a._id}>{a.name}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label>Frecuencia</Label>
                  <Input type="select" value={form.frecuencia} onChange={(e) => cambiaForm({ frecuencia: e.target.value })}>
                    {FRECUENCIAS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label>Primera fecha</Label>
                  <Input type="date" value={form.fechaInicio} onChange={(e) => cambiaForm({ fechaInicio: e.target.value })} />
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Label>¿Lleva IVA?</Label>
                  <Input type="select" value={form.llevaIva} onChange={(e) => cambiaForm({ llevaIva: e.target.value })}>
                    <option value="Si">Sí (16%)</option>
                    <option value="No">No</option>
                    <option value="Manual">Otro monto</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Label>Subtotal estimado</Label>
                  <Input type="number" min="0" step="any" value={form.subtotal} onChange={(e) => cambiaForm({ subtotal: e.target.value })} />
                </Col>
                <Col md={3}>
                  <Label>IVA</Label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={form.iva}
                    disabled={form.llevaIva !== "Manual"}
                    onChange={(e) => cambiaForm({ iva: e.target.value })}
                  />
                </Col>
                <Col md={3}>
                  <Label>Total estimado</Label>
                  <Input disabled value={money(Number(form.subtotal) + Number(form.iva))} />
                </Col>
              </Row>
              <div style={{ fontSize: 12, color: "#6c757d", marginTop: 8 }}>
                El monto es estimado: al registrar cada periodo se puede cambiar por el real.
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="success" onClick={guardarForm}>
                Guardar
              </Button>
              <Button color="secondary" onClick={() => setModal(false)}>
                Cancelar
              </Button>
            </ModalFooter>
          </Modal>
        </div>
      ) : undefined}
    </>
  );
}

export default GastosRecurrentes;
